import type { AmplitudePropertiesMap } from '../../amplitude-properties.types';
import { getMappedEventProperties } from '../../utils';
import { REWARDS_HUB_AMP_EVENT, REWARDS_MODULE } from './event-names';
import type {
    BonusSpinsCardClickedTrackingProps,
    BonusCashCardClickedTrackingProps,
    RewardMachineCardClickedTrackingProps
} from '@fanduel/rewards-hub-client';

type TTrackingProps = (event: string, props?: Record<string, unknown>) => void;

type RewardTypeProps = {
    rewardId?: string;
    rewardType?: string;
};

type RewardBoxDetailProps = {
    isLocked?: boolean;
    rewardBoxType?: string;
    unlockTimestamp?: string;
    countdownDuration?: number;
    module?: string;
    interactionType?: string;
};

type RewardCardCommonProps = RewardBoxDetailProps & {
    position?: number;
    rewardName?: string;
    rewardId?: string;
};

type RewardsBoxErrorProps = RewardTypeProps & { errorReason: string };

type RewardsBoxAnimationCloseProps = RewardTypeProps & {
    animationState: 'animation_completed' | 'animation_playing';
    interactionType: 'x_button' | 'background_tap';
};

type RewardsBoxOpenProps = RewardTypeProps & RewardBoxDetailProps & { rewardAmount?: number };

type RewardsBoxClickProps = RewardCardCommonProps & { rewardsCount?: number };

type RewardsCountProps = { rewardsCount: number };

type RewardModuleErrorProps = {
    errorReason?: string;
    position?: number;
    rewardName?: string;
    rewardId?: string;
};

export const mapPropNames = (props: Record<string, unknown>) =>
    getMappedEventProperties(props as Partial<AmplitudePropertiesMap>);

export const getRewardBoxTrackingEvents = (track: TTrackingProps, parentModule: string) => {
    return {
        handleRewardsBoxCtaClickError: (props: RewardTypeProps) =>
            track(REWARDS_HUB_AMP_EVENT.REWARDS_BOX_ERROR_CTA_CLICKED, {
                ...mapPropNames(props),
                Module: REWARDS_MODULE.RB_ERROR_MODAL
            }),
        handleRewardsBoxError: (props: RewardsBoxErrorProps) =>
            track(REWARDS_HUB_AMP_EVENT.REWARDS_BOX_ERROR, {
                ...mapPropNames(props),
                Module: REWARDS_MODULE.RB_ERROR_MODAL
            }),
        handleRewardsBoxAnimationClose: (props: RewardsBoxAnimationCloseProps) =>
            track(REWARDS_HUB_AMP_EVENT.REWARDS_BOX_ANIMATION_CLOSED, {
                ...mapPropNames(props),
                Module: REWARDS_MODULE.RB_ANIMATION
            }),
        handleRewardsBoxOpen: (props: RewardsBoxOpenProps) =>
            track(REWARDS_HUB_AMP_EVENT.REWARDS_BOX_OPENED, {
                Module: REWARDS_MODULE.RB_ANIMATION,
                ...mapPropNames(props)
            }),
        handleRewardsBoxClick: (props: RewardsBoxClickProps) =>
            track(REWARDS_HUB_AMP_EVENT.REWARDS_MODULE_LINK_CLICKED, {
                Module: parentModule,
                ...mapPropNames({ ...props })
            }),
        handleRewardsBoxCarouselScrolled: (props: RewardsCountProps) =>
            track(REWARDS_HUB_AMP_EVENT.REWARDS_BOX_CAROUSEL_SCROLLED, {
                ...mapPropNames(props),
                Module: REWARDS_MODULE.REWARDS_HUB
            })
    };
};

export const getRewardCardTrackingEvents = (track: TTrackingProps) => {
    return {
        handleRewardModuleViewed: (props: RewardCardCommonProps) =>
            track(REWARDS_HUB_AMP_EVENT.REWARDS_MODULE_VIEWED, { ...mapPropNames(props) }),
        handleRewardModuleError: (props: RewardModuleErrorProps) =>
            track(REWARDS_HUB_AMP_EVENT.REWARDS_MODULE_ERROR, { ...mapPropNames(props) }),
        handleRewardModuleRefreshed: (props: RewardModuleErrorProps) =>
            track(REWARDS_HUB_AMP_EVENT.REWARDS_MODULE_REFRESHED, { ...mapPropNames(props) })
    };
};

export const getCasinoBonusRewardCardTrackingEvents = (track: TTrackingProps) => {
    return {
        handleBonusCashCardClicked: (props: BonusCashCardClickedTrackingProps) => {
            track(REWARDS_HUB_AMP_EVENT.REWARDS_MODULE_LINK_CLICKED, { ...mapPropNames(props) });
        }
    };
};

export const getBonusSpinsRewardCardTrackingEvents = (track: TTrackingProps) => {
    return {
        handleBonusSpinsCardClicked: (props: BonusSpinsCardClickedTrackingProps) => {
            track(REWARDS_HUB_AMP_EVENT.REWARDS_MODULE_LINK_CLICKED, { ...mapPropNames(props) });
        }
    };
};

export const getRewardMachineCardTrackingEvents = (track: TTrackingProps) => {
    return {
        handleRewardMachineCardClicked: (props: RewardMachineCardClickedTrackingProps) => {
            track(REWARDS_HUB_AMP_EVENT.REWARDS_MODULE_LINK_CLICKED, { ...mapPropNames(props) });
        }
    };
};
